import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardInstance } from '@hub/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ActionButton, Alert, Card, Loading, StateDot, useFlash } from '../components/ui';

interface DashboardData {
  instances: DashboardInstance[];
  limits: { maxInstances: number; used: number };
}

export function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logsFor, setLogsFor] = useState<number | null>(null);
  const [logs, setLogs] = useState('');
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      setData(await api.get<DashboardData>('/api/dashboard'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the dashboard');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggleLogs = useCallback(async (id: number) => {
    if (logsFor === id) { setLogsFor(null); return; }
    setLogsFor(id);
    setLogs('Loading…');
    try {
      const res = await api.get<{ logs: string }>(`/api/instances/${id}/logs?tail=200`);
      setLogs(res.logs || '(no output)');
    } catch (err) {
      setLogs(err instanceof Error ? err.message : 'Could not read logs');
    }
  }, [logsFor]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (!data) return <Loading />;

  const atLimit = data.limits.used >= data.limits.maxInstances;

  return (
    <>
      {user && !user.emailVerified && (
        <div className="alert-warn" style={{ marginBottom: '1.5rem', fontSize: '0.85rem' }}>
          Check your inbox to verify your email address. Unverified accounts cannot use the REST API with a password.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Your services</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {flash.node}
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            {data.limits.used} of {data.limits.maxInstances} used
          </span>
          <Link
            to="/dashboard/new"
            className="btn-primary"
            style={atLimit ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            title={atLimit ? 'You have reached your instance limit' : undefined}
          >
            ＋ New service
          </Link>
        </div>
      </div>

      {data.instances.length === 0 ? (
        <Card>
          <p className="muted" style={{ margin: 0 }}>
            No services yet. <Link to="/dashboard/new">Create your first one</Link>.
          </p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {data.instances.map((inst) => (
            <Card key={inst.id} style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ background: inst.bgColor, padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.25rem' }}>{inst.productIcon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: inst.accentColor, fontWeight: 600, fontSize: '0.95rem' }}>{inst.name}</div>
                  <div className="mono" style={{ fontSize: '0.75rem', opacity: 0.85, color: inst.accentColor }}>
                    {inst.subdomain}
                  </div>
                </div>
                <StateDot state={inst.status} />
              </div>

              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <a className="btn-ghost btn-sm" href={inst.rootUrl} target="_blank" rel="noreferrer">Open</a>
                  <Link className="btn-ghost btn-sm" to={`/dashboard/instances/${inst.id}`}>Settings</Link>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => { void toggleLogs(inst.id); }}>
                    Logs
                  </button>
                  {inst.status === 'running' ? (
                    <ActionButton
                      busyLabel="Stopping…"
                      onClick={async () => { await api.post(`/api/instances/${inst.id}/stop`); await load(); }}
                    >Stop</ActionButton>
                  ) : (
                    <ActionButton
                      busyLabel="Starting…"
                      onClick={async () => { await api.post(`/api/instances/${inst.id}/start`); await load(); }}
                    >Start</ActionButton>
                  )}
                  <ActionButton
                    danger
                    busyLabel="Deleting…"
                    confirm={`Delete ${inst.name}? This removes the container and its data permanently.`}
                    onClick={async () => {
                      await api.delete(`/api/instances/${inst.id}`);
                      flash.show(`${inst.name} deleted`, 'ok');
                      await load();
                    }}
                  >Delete</ActionButton>
                </div>

                {logsFor === inst.id && <pre className="output">{logs}</pre>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
